import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  VStack,
  SimpleGrid,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Box,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { getData } from '../../utils/httpClient.js';
import { formatNumber } from '../../utils/transform';

function formatDate(value) {
  if (!value) return '—';
  return String(value).slice(0, 10).split('-').reverse().join('/');
}

const STATUS_SCHEME = {
  Pendiente: 'orange',
  Pagado: 'green',
  Parcial: 'yellow',
};

function getLineProductName(line) {
  return (
    line.product?.name ||
    line.productName ||
    (line.productId ? `#${line.productId}` : '—')
  );
}

function getLineProductCode(line) {
  const code = line.product?.code;
  if (code == null) return '';
  return String(code).trim();
}

export default function PurchaseInvoiceDetailModal({ isOpen, onClose, invoiceId }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setInvoice(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const encoded = encodeURIComponent(invoiceId);
        const response = await getData(`/provider-invoices/${encoded}`);
        if (!cancelled) {
          setInvoice(response?.payload?.invoice || null);
        }
      } catch (error) {
        if (!cancelled) {
          setInvoice(null);
          toast({
            title: 'No se pudo cargar la factura',
            description: error?.message || 'Error desconocido',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, invoiceId, toast]);

  const details = invoice?.providerInvoiceDetails || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          Factura de compra {invoiceId || ''}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading && (
            <Box py={8} textAlign="center">
              <Spinner />
            </Box>
          )}

          {!loading && !invoice && (
            <Text color="gray.500" textAlign="center" py={6}>
              Sin datos de la factura
            </Text>
          )}

          {!loading && invoice && (
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Proveedor
                  </Text>
                  <Text fontWeight="medium">{invoice.provider?.name || '—'}</Text>
                  {invoice.provider?.rut && (
                    <Text fontSize="sm" color="gray.600">
                      RUT: {invoice.provider.rut}
                    </Text>
                  )}
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Estado
                  </Text>
                  <Badge colorScheme={STATUS_SCHEME[invoice.status] || 'gray'}>
                    {invoice.status || '—'}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Fecha
                  </Text>
                  <Text>{formatDate(invoice.date)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Vencimiento
                  </Text>
                  <Text>{formatDate(invoice.expirationDate)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Total
                  </Text>
                  <Text fontWeight="bold" color="green.600">
                    {invoice.totalAmount != null
                      ? formatNumber(Number(invoice.totalAmount))
                      : '—'}
                    {invoice.currency?.code ? ` ${invoice.currency.code}` : ''}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Tipo
                  </Text>
                  <Text>{invoice.type || '—'}</Text>
                </Box>
              </SimpleGrid>

              {invoice.payment && (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Pago asociado
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} fontSize="sm">
                      <Text>
                        Fecha: {formatDate(invoice.payment.paymentDate)}
                      </Text>
                      <Text>
                        Monto:{' '}
                        {invoice.payment.paymentAmount != null
                          ? formatNumber(Number(invoice.payment.paymentAmount))
                          : '—'}
                      </Text>
                      <Text>
                        Ref.: {invoice.payment.paymentReference || '—'}
                      </Text>
                    </SimpleGrid>
                  </Box>
                </>
              )}

              <Divider />

              <Text fontSize="sm" fontWeight="semibold">
                Detalle
              </Text>
              {details.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Sin líneas de detalle registradas
                </Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th whiteSpace="nowrap">Código</Th>
                        <Th>Producto</Th>
                        <Th isNumeric>Cant.</Th>
                        <Th isNumeric>Costo</Th>
                        <Th isNumeric>Total</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {details.map((line) => {
                        const qty = Number(line.quantity) || 0;
                        const cost = Number(line.cost) || 0;
                        const lineTotal =
                          line.totalWoTax != null
                            ? Number(line.totalWoTax)
                            : qty * cost;
                        const code = getLineProductCode(line);
                        const name = getLineProductName(line);
                        return (
                          <Tr key={line.id}>
                            <Td whiteSpace="nowrap" fontSize="sm" color="gray.700">
                              {code || '—'}
                            </Td>
                            <Td fontSize="sm">{name}</Td>
                            <Td isNumeric>{qty}</Td>
                            <Td isNumeric>{formatNumber(cost)}</Td>
                            <Td isNumeric>{formatNumber(lineTotal)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
